"use client";

import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableRowProps, VirtualTableViewProps } from "./types";

import { TableView } from "./tableView";

// 使用 ES6 类封装可视行状态管理
class VisibleRowsManager {
    private indices: Set<number>;
    private _minIndex: number;
    private _maxIndex: number;

    constructor() {
        this.indices = new Set<number>();
        this._minIndex = -1;
        this._maxIndex = -1;
    }

    get minIndex(): number {
        return this._minIndex;
    }

    get maxIndex(): number {
        return this._maxIndex;
    }

    updateIndices(index: number, isVisible: boolean): void {
        if (isVisible) {
            this.indices.add(index);
        } else {
            this.indices.delete(index);
        }
        
        this.recalculateRange();
    }

    private recalculateRange(): void {
        if (this.indices.size === 0) {
            this._minIndex = -1;
            this._maxIndex = -1;
            return;
        }
        
        const validIndices = Array.from(this.indices).filter(idx => idx >= 0);
        if (validIndices.length > 0) {
            this._minIndex = Math.min(...validIndices);
            this._maxIndex = Math.max(...validIndices);
        } else {
            this._minIndex = -1;
            this._maxIndex = -1;
        }
    }

    getVisibleRange(): { min: number; max: number } {
        return { min: this._minIndex, max: this._maxIndex };
    }

    getVisibleIndices(): number[] {
        return Array.from(this.indices)
            .filter(idx => idx >= 0)
            .sort((a, b) => a - b);
    }

    shouldBeVisible(index: number, preloadRowCount: number): boolean {
        // 如果没有可见行，显示所有行
        if (this._minIndex === -1 || this._maxIndex === -1) {return true;}
        
        // 计算预加载范围
        const minWithPreload = Math.max(0, this._minIndex - preloadRowCount);
        const maxWithPreload = this._maxIndex + preloadRowCount;
        
        // 如果索引在预加载范围内，则应该可见
        return index >= minWithPreload && index <= maxWithPreload;
    }
}

// 创建单例实例


// 自定义 Row 组件，用于检测行是否在可视区域内
const CustomRow = (props: PropsWithChildren<any> & TableRowProps & { dataIndex: number, visibleRowsManager: VisibleRowsManager }) => {
    const { children, rowHeight = 60, preloadRowCount, dataIndex, visibleRowsManager, ...restProps } = props;
    const rowRef = useRef<HTMLTableRowElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const rowIndex = props['data-row-index'] as number;


    // 创建 observer 的函数，便于窗口调整时重新创建
    const createObserver = useCallback(() => {
        // 检查环境是否支持 IntersectionObserver
        if (typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            visibleRowsManager.updateIndices(rowIndex, true);
            return null;
        }

        try {
            // 计算需要预加载的行高
            const preloadMargin = `${rowHeight * preloadRowCount}px`;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    const isIntersecting = entry.isIntersecting;
                    setIsVisible(isIntersecting);
                    
                    // 更新全局状态
                    visibleRowsManager.updateIndices(rowIndex, isIntersecting);
                },
                {
                    threshold: 0.1, // 当至少 10% 的行可见时，认为它在可视区域内
                    // 设置 rootMargin 使得在实际可视区域上下各扩展预加载区域
                    rootMargin: `${preloadMargin} 0px ${preloadMargin} 0px`,
                },
            );

            return observer;
        } catch (error) {
            // 出错时默认显示所有行
            setIsVisible(true);
            visibleRowsManager.updateIndices(rowIndex, true);
            return null;
        }
    }, [rowHeight, preloadRowCount, rowIndex, visibleRowsManager]);

    // 检查是否应该根据预加载范围显示
    useEffect(() => {
        // 如果行已经通过 IntersectionObserver 判断为可见，则不需要额外检查
        if (isVisible) {return;}
        
        // 检查是否在预加载范围内
        const shouldBeVisible = visibleRowsManager.shouldBeVisible(rowIndex, preloadRowCount);
        if (shouldBeVisible !== isVisible) {
            setIsVisible(shouldBeVisible);
        }
    }, [isVisible, rowIndex, preloadRowCount, visibleRowsManager]);

    // 初始化和清理 observer
    useEffect(() => {
        // 服务器端渲染时不执行
        if (typeof window === 'undefined') {return;}

        const currentRef = rowRef.current;
        if (!currentRef) {return;}

        // 创建 observer
        observerRef.current = createObserver();
        
        // 如果成功创建了 observer，开始观察
        if (observerRef.current) {
            observerRef.current.observe(currentRef);
        }

        return () => {
            // 清理 observer
            if (observerRef.current && currentRef) {
                observerRef.current.unobserve(currentRef);
                observerRef.current.disconnect();
            }
            
            // 从全局状态中移除此行
            visibleRowsManager.updateIndices(rowIndex, false);
        };
    }, [createObserver, rowIndex, visibleRowsManager]);

    // 处理窗口大小调整
    useEffect(() => {
        // 服务器端渲染时不执行
        if (typeof window === 'undefined') {return;}

        const handleResize = () => {
            const currentRef = rowRef.current;
            if (!currentRef) {return;}

            // 清理旧的 observer
            if (observerRef.current) {
                observerRef.current.unobserve(currentRef);
                observerRef.current.disconnect();
            }

            // 创建新的 observer
            observerRef.current = createObserver();
            
            // 如果成功创建了 observer，开始观察
            if (observerRef.current) {
                observerRef.current.observe(currentRef);
            }
        };

        // 添加窗口大小调整事件监听
        window.addEventListener('resize', handleResize);

        return () => {
            // 移除事件监听
            window.removeEventListener('resize', handleResize);
        };
    }, [createObserver]);

    // 如果行不可见，可以在这里通过某种方式将其索引暴露为 null
    // 例如，可以通过 data-visible 属性来标记行的可见性
    return (
        <tr
            ref={rowRef}
            {...restProps}
            data-visible={isVisible}
            style={{ ...restProps.style, height: rowHeight }}
            data-row-index={isVisible ? rowIndex : null}
        >
            {isVisible ? children : null}
        </tr>
    );
};



export const VirtualTableView = <T extends Record<string, any>>(props: VirtualTableViewProps<T>) => {
    const { rowHeight, preloadRowCount = 4, onRow, pagination, ...restProps } = props;
    
    // 表格容器引用，用于滚动到顶部
    const tableContainerRef = useRef<HTMLDivElement>(null);
    
    // 使用 useMemo 缓存 visibleRowsManager 实例，避免不必要的重新渲染
    const visibleRowsManager = useMemo(() => {
        return new VisibleRowsManager()
    }, []);
    
    // 定义自定义组件
    const components: VirtualTableViewProps<T>['components'] = useMemo(() => {
        return {
            body: {
                row: (rowProps) => {
                    return <CustomRow 
                        {...rowProps} 
                        rowHeight={rowHeight} 
                        visibleRowsManager={visibleRowsManager}
                        preloadRowCount={preloadRowCount}
                    />;
                },
            },
        };
    }, [rowHeight, preloadRowCount, visibleRowsManager]);

    // 滚动到表格顶部的函数
    const scrollToTop = useCallback(() => {
        if (!tableContainerRef.current) {
            return;
        }

        
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
            try {
                const container = tableContainerRef.current;
                if (!container) {
                    return;
                }

                // First try: Find the Ant Design table scroll container
                const tableBody = container.querySelector('.ant-table-body') as HTMLElement;
                if (tableBody?.scrollHeight > tableBody.clientHeight) {
                    tableBody.scrollTop = 0;
                    return;
                }
                
                // Second try: Find nearest scrollable parent
                let scrollableParent = container.parentElement;
                while (scrollableParent && scrollableParent !== document.body) {
                    if (scrollableParent.scrollHeight > scrollableParent.clientHeight) {
                        const tableTop = container.getBoundingClientRect().top;
                        const parentTop = scrollableParent.getBoundingClientRect().top;
                        const offset = tableTop - parentTop + scrollableParent.scrollTop;

                        // Larger offset for header visibility (increased from 20 to 120)
                        scrollableParent.scrollTop = Math.max(0, offset - 120);
                        return;
                    }
                    scrollableParent = scrollableParent.parentElement;
                }
                
                // Final fallback: Use scrollIntoView with additional offset
                container.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
                
                // Add extra upward scroll after scrollIntoView
                setTimeout(() => {
                    try {
                        window.scrollBy({
                            top: -120, // Scroll up 120px to expose header
                            behavior: 'smooth',
                        });
                    } catch (error) {
                        // Error in additional scroll offset
                    }
                }, 300);
                
                
            } catch (error) {
                // Error in scrollToTop
            }
        });
    }, []);

    // 处理分页变化，添加滚动到顶部的功能
    const handlePaginationChange = useCallback((page: number, pageSize?: number) => {
        try {
            // 调用原始的分页变化回调
            if (pagination && typeof pagination === 'object' && pagination.onChange) {
                pagination.onChange(page, pageSize || 10);
            }
            
            // 滚动到顶部
            scrollToTop();
        } catch (error) {
            // Error in handlePaginationChange
        }
    }, [pagination, scrollToTop]);

    // 处理页面大小变化
    const handleShowSizeChange = useCallback((current: number, size: number) => {
        try {
            // 调用原始的页面大小变化回调
            if (pagination && typeof pagination === 'object' && pagination.onShowSizeChange) {
                pagination.onShowSizeChange(current, size);
            }
            
            // 滚动到顶部
            scrollToTop();
        } catch (error) {
            // Error in handleShowSizeChange
        }
    }, [pagination, scrollToTop]);

    // 处理分页配置
    const enhancedPagination = useMemo(() => {
        if (!pagination) {
            return pagination;
        }
        
        if (typeof pagination === 'boolean') {
            return pagination;
        }
        
        return {
            ...pagination,
            onChange: handlePaginationChange,
            onShowSizeChange: handleShowSizeChange,
        };
    }, [pagination, handlePaginationChange, handleShowSizeChange]);

    return (
        <div ref={tableContainerRef}>
            <TableView
                {...restProps}
                pagination={enhancedPagination}
                components={components}
                onRow={(record, index) => {
                    // 返回正确的类型，确保与 HTMLAttributes 兼容
                    return {
                        'data-row-index': index,
                        style: {}, // 添加一个空的 style 对象以确保类型兼容
                        ...onRow?.(record, index),
                    };
                }}
            />
        </div>
    );
}